import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { commitMove } from "../lib/crypto";
import { sfx } from "../lib/fx";
import { toast } from "sonner";
import { Bot, Clock, Repeat } from "lucide-react";
import { RockIcon, PaperIcon, ScissorsIcon, getRpsIcon } from "../components/RpsIcons";
import StreakBadges from "../components/StreakBadges";

const MOVES = [
  { key: "rock", label: "Rock", Icon: RockIcon, ring: "border-red-400/60", bg: "bg-red-500/10", text: "text-red-300", glow: "glow-red" },
  { key: "paper", label: "Paper", Icon: PaperIcon, ring: "border-cyan-400/60", bg: "bg-cyan-500/10", text: "text-cyan-300", glow: "glow-cyan" },
  { key: "scissors", label: "Scissors", Icon: ScissorsIcon, ring: "border-amber-400/60", bg: "bg-amber-500/10", text: "text-amber-300", glow: "glow-amber" },
];

const TIMEOUT_STYLE = {
  ring: "border-slate-600",
  bg: "bg-slate-800/60",
  text: "text-slate-400",
  glow: "",
};

export default function Game({ ws, matchData, onEnd, user }) {
  const [phase, setPhase] = useState("choose"); // choose | committed | reveal | clash | resolved | done
  const [clashStage, setClashStage] = useState("enter"); // enter | impact
  const [myMove, setMyMove] = useState(null);
  const [oppCommitted, setOppCommitted] = useState(false);
  const [roundResult, setRoundResult] = useState(null);
  const [score, setScore] = useState({ p1: 0, p2: 0 });
  const [roundNum, setRoundNum] = useState(1);
  const [matchResult, setMatchResult] = useState(null);
  const [opponentLeft, setOpponentLeft] = useState(false);
  const [deadlineMs, setDeadlineMs] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [rematchSent, setRematchSent] = useState(false);
  const [rematchOffered, setRematchOffered] = useState(false);

  const yourKey = matchData.your_role;
  const oppKey = yourKey === "p1" ? "p2" : "p1";

  const commitStateRef = useRef({ move: null, nonce: null });

  // countdown ticker
  useEffect(() => {
    if (!deadlineMs) return undefined;
    const tick = () => {
      const rem = Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
      setRemaining(rem);
      if (phase === "choose" && !myMove && rem > 0 && rem <= 5) sfx.urgent();
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [deadlineMs, phase, myMove]);

  useEffect(() => {
    const handler = async (evt) => {
      const msg = JSON.parse(evt.data);
      switch (msg.type) {
        case "opponent_committed":
          setOppCommitted(true);
          break;
        case "reveal_phase":
          if (commitStateRef.current.move) {
            ws.send(JSON.stringify({
              type: "reveal",
              move: commitStateRef.current.move,
              nonce: commitStateRef.current.nonce,
            }));
            setPhase("reveal");
          }
          break;
        case "round_start":
          setRoundNum(msg.round);
          setScore(msg.score);
          setMyMove(null);
          setOppCommitted(false);
          setRoundResult(null);
          commitStateRef.current = { move: null, nonce: null };
          setPhase("choose");
          setDeadlineMs(msg.deadline_ms || null);
          break;
        case "round_result": {
          setScore(msg.score);
          const rr = {
            p1: msg.p1_move,
            p2: msg.p2_move,
            winner: msg.winner,
            forfeit: msg.forfeit || false,
          };
          setRoundResult(rr);
          setPhase("clash");
          setClashStage("enter");
          sfx.reveal();
          // impact + outcome sound after hands fly in (~550ms)
          setTimeout(() => {
            setClashStage("impact");
            if (rr.winner === yourKey) sfx.roundWin();
            else if (rr.winner === oppKey) sfx.roundLose();
            else sfx.roundTie();
          }, 550);
          // transition to per-round result card, but only if the match hasn't already ended
          // (match_end can arrive right after the last round_result and set phase="done").
          setTimeout(() => {
            setPhase((cur) => (cur === "clash" ? "resolved" : cur));
          }, 1400);
          break;
        }
        case "match_end":
          setMatchResult({ winner: msg.winner, final_score: msg.final_score });
          setPhase("done");
          if (msg.winner === yourKey) sfx.win();
          else if (msg.winner === "tie") sfx.tie();
          else sfx.lose();
          break;
        case "opponent_left":
          setOpponentLeft(true);
          setMatchResult({ winner: yourKey, final_score: score, forfeit: true });
          setPhase("done");
          sfx.win();
          break;
        case "rematch_offered":
          setRematchOffered(true);
          toast.info("Opponent wants a rematch!");
          break;
        case "rematch_ack":
          setRematchSent(true);
          break;
        case "rematch_cancelled":
          setRematchSent(false);
          setRematchOffered(false);
          toast.error("Rematch cancelled — opponent left.");
          break;
        default:
          break;
      }
    };
    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
    // eslint-disable-next-line
  }, [ws]);

  const chooseMove = async (moveKey) => {
    if (phase !== "choose" || myMove) return;
    const { commitment, nonce } = await commitMove(moveKey);
    setMyMove(moveKey);
    commitStateRef.current = { move: moveKey, nonce };
    ws.send(JSON.stringify({ type: "commit", commitment }));
    setPhase("committed");
    sfx.commit();
    toast.success(`Locked in ${moveKey}`);
  };

  const myScore = score[yourKey];
  const oppScore = score[oppKey];

  const yourWon = roundResult && roundResult.winner === yourKey;
  const yourLost = roundResult && roundResult.winner === oppKey;
  const isTie = roundResult && roundResult.winner === "tie";

  const getMoveDisplay = (moveKey) => {
    const known = MOVES.find((m) => m.key === moveKey);
    if (known) return { Icon: known.Icon, label: known.label.toUpperCase(), ...known };
    // forfeit / timeout / hidden-committed
    if (moveKey === "committed") return { Icon: Clock, label: "TIMED OUT", ...TIMEOUT_STYLE };
    return { Icon: Clock, label: "NO MOVE", ...TIMEOUT_STYLE };
  };

  const yourDisp = roundResult && getMoveDisplay(roundResult[yourKey]);
  const oppDisp = roundResult && getMoveDisplay(roundResult[oppKey]);

  const matchWon = matchResult && matchResult.winner === yourKey;
  const matchTie = matchResult && matchResult.winner === "tie";

  // Timer bar percent
  const totalMs = 15000;
  const pctLeft = deadlineMs
    ? Math.max(0, Math.min(100, ((deadlineMs - Date.now()) / totalMs) * 100))
    : 0;
  const showTimer = phase === "choose" || phase === "committed";
  const timerColor = remaining <= 5 ? "bg-red-400" : remaining <= 10 ? "bg-amber-400" : "bg-cyan-400";

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="chip chip-slate">ROUND {roundNum} · BEST OF 3</div>
          <div className="font-mono-stat text-sm text-slate-500" data-testid="match-id">
            #{matchData.match_id.slice(0, 6)}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 items-center">
          <PlayerCard
            name={user.username}
            score={myScore}
            you
            testId="player-you"
            currentStreak={user.current_streak}
            bestStreak={user.best_streak}
          />
          <div className="text-center font-display font-black text-4xl sm:text-5xl text-neon-cyan">VS</div>
          <PlayerCard name={matchData.opponent.username} score={oppScore} bot={matchData.opponent.is_bot} testId="player-opponent" />
        </div>

        {/* Round timer */}
        {showTimer && (
          <div className="neo-flat p-3 flex items-center gap-3" data-testid="round-timer">
            <Clock className="w-4 h-4 text-slate-400" />
            <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className={`h-full ${timerColor} transition-all duration-200 ease-linear`}
                style={{ width: `${pctLeft}%` }}
              />
            </div>
            <div className="font-mono-stat text-sm text-slate-300 w-8 text-right" data-testid="round-timer-seconds">
              {remaining}s
            </div>
          </div>
        )}

        {phase === "clash" && roundResult && (
          <div className="neo-flat p-6 sm:p-10 min-h-[260px] flex items-center justify-center relative overflow-hidden" data-testid="round-clash">
            <div className="grid grid-cols-3 gap-4 items-center w-full relative">
              <div className="flex justify-center" data-testid="clash-your-side">
                <div
                  className={`clash-left ${clashStage === "impact" && yourWon ? "winner-pulse" : ""} ${clashStage === "impact" && yourLost ? "loser-shrink" : ""} ${clashStage === "impact" && isTie ? "tie-shake-clash" : ""}`}
                >
                  <div className={`p-5 rounded-2xl ${yourDisp.bg} border ${yourDisp.ring} ${yourDisp.text}`}>
                    <yourDisp.Icon size={80} />
                  </div>
                </div>
              </div>
              <div className="text-center relative">
                <div className={`font-display font-black text-3xl sm:text-4xl ${clashStage === "impact" ? (yourWon ? "text-neon-lime" : yourLost ? "text-neon-red" : "text-slate-400") : "text-slate-500"}`}>
                  {clashStage === "impact"
                    ? (yourWon ? "WIN" : yourLost ? "LOSS" : "TIE")
                    : "VS"}
                </div>
                {clashStage === "impact" && (
                  <div
                    className={`impact-burst ${yourWon ? "burst-gold" : yourLost ? "burst-red" : "burst-neutral"}`}
                    aria-hidden
                    data-testid="clash-impact-burst"
                  />
                )}
              </div>
              <div className="flex justify-center" data-testid="clash-opponent-side">
                <div
                  className={`clash-right ${clashStage === "impact" && yourLost ? "winner-pulse" : ""} ${clashStage === "impact" && yourWon ? "loser-shrink" : ""} ${clashStage === "impact" && isTie ? "tie-shake-clash" : ""}`}
                >
                  <div className={`p-5 rounded-2xl ${oppDisp.bg} border ${oppDisp.ring} ${oppDisp.text}`}>
                    <oppDisp.Icon size={80} />
                  </div>
                </div>
              </div>
            </div>
            {clashStage === "impact" && (
              <div
                className={`screen-flash ${yourWon ? "flash-gold" : yourLost ? "flash-red" : "flash-neutral"}`}
                aria-hidden
                data-testid="clash-screen-flash"
              />
            )}
          </div>
        )}

        {phase === "resolved" && roundResult && (
          <div className="neo-flat p-6 sm:p-8 text-center space-y-4" data-testid="round-result-card">
            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="space-y-2" data-testid="your-move-reveal">
                <div className={`shake p-5 rounded-2xl ${yourDisp.bg} border ${yourDisp.ring} ${yourDisp.text} inline-flex`}>
                  <yourDisp.Icon size={64} />
                </div>
                <div className="font-mono-stat text-xs text-slate-400">YOU</div>
                <div className={`font-display font-bold ${yourDisp.text}`}>{yourDisp.label}</div>
              </div>
              <div className="font-display font-black text-3xl sm:text-5xl">
                {yourWon && <span className="text-neon-lime">WIN</span>}
                {yourLost && <span className="text-neon-red">LOSS</span>}
                {isTie && <span className="text-slate-400">TIE</span>}
              </div>
              <div className="space-y-2" data-testid="opponent-move-reveal">
                <div className={`shake p-5 rounded-2xl ${oppDisp.bg} border ${oppDisp.ring} ${oppDisp.text} inline-flex`}>
                  <oppDisp.Icon size={64} />
                </div>
                <div className="font-mono-stat text-xs text-slate-400">OPPONENT</div>
                <div className={`font-display font-bold ${oppDisp.text}`} data-testid="opponent-move-label">
                  {oppDisp.label}
                </div>
              </div>
            </div>
            <div className="text-slate-400 font-semibold">
              {roundResult.forfeit && "Timeout — round resolved by forfeit."}
              {!roundResult.forfeit && yourWon && "You take the round."}
              {!roundResult.forfeit && yourLost && "Round goes to them."}
              {!roundResult.forfeit && isTie && "Even hands. Next round…"}
            </div>
          </div>
        )}

        {(phase === "choose" || phase === "committed" || phase === "reveal") && (
          <div className="neo-flat p-6 sm:p-8 space-y-6" data-testid="move-picker">
            <div className="text-center">
              <div className="font-display font-black text-2xl sm:text-3xl text-slate-50">
                {phase === "choose" && "Make your move"}
                {phase === "committed" && "Locked in. Waiting for opponent…"}
                {phase === "reveal" && "Revealing…"}
              </div>
              <div className="text-sm text-slate-400 mt-1 font-mono-stat">
                {phase === "committed" && (oppCommitted ? "Opponent locked. Revealing…" : "Opponent still choosing…")}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              {MOVES.map((m) => {
                const selected = myMove === m.key;
                const disabled = phase !== "choose" || myMove;
                return (
                  <button
                    key={m.key}
                    onClick={() => chooseMove(m.key)}
                    disabled={disabled}
                    data-testid={`move-${m.key}-button`}
                    className={`aspect-square rounded-2xl border transition font-display font-bold text-lg flex flex-col items-center justify-center gap-3
                      ${selected ? `${m.bg} ${m.ring} ${m.text} ${m.glow}` : "bg-slate-900/60 border-slate-700 text-slate-300 hover:border-cyan-400/60 hover:text-cyan-200"}
                      ${disabled && !selected ? "opacity-40" : ""} disabled:cursor-not-allowed`}
                  >
                    <m.Icon size={64} />
                    <div className="tracking-wide text-xs sm:text-sm">{m.label.toUpperCase()}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {phase === "done" && matchResult && (
          <div className={`neo-flat p-8 text-center space-y-4 ${matchWon ? "glow-lime" : matchTie ? "" : "glow-red"}`} data-testid="match-end-card">
            <div className="font-display font-black text-5xl sm:text-6xl tracking-tighter">
              {matchWon && <span className="text-neon-lime">VICTORY</span>}
              {matchTie && <span className="text-slate-400">DRAW</span>}
              {!matchWon && !matchTie && <span className="text-neon-red">DEFEAT</span>}
            </div>
            {opponentLeft && <div className="text-slate-400">Opponent left the match.</div>}
            <div className="font-mono-stat text-sm text-slate-400">
              FINAL: {matchResult.final_score[yourKey]} – {matchResult.final_score[oppKey]}
            </div>
            {matchData.private && (
              <div className="font-mono-stat text-[10px] text-slate-500" data-testid="friend-match-disclaimer">
                FRIEND MATCH · DOES NOT COUNT TOWARD LEADERBOARD
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              {matchData.private && !opponentLeft && (
                <Button
                  onClick={() => {
                    ws.send(JSON.stringify({ type: "rematch", match_id: matchData.match_id }));
                    setRematchSent(true);
                  }}
                  disabled={rematchSent}
                  data-testid="rematch-button"
                  className="border border-violet-400/60 bg-violet-400 hover:bg-violet-300 text-slate-950 disabled:opacity-60"
                >
                  <Repeat className="w-4 h-4 mr-2" />
                  {rematchSent
                    ? rematchOffered ? "Both ready — starting…" : "Waiting for opponent…"
                    : rematchOffered ? "Accept Rematch" : "Rematch"}
                </Button>
              )}
              <Button
                onClick={onEnd}
                data-testid="return-lobby-button"
                variant="outline"
                className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
              >
                Back to Lobby
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerCard({ name, score, you, bot, testId, currentStreak, bestStreak }) {
  return (
    <div className={`neo-flat p-4 text-center ${you ? "glow-amber" : ""}`} data-testid={testId}>
      <div className="font-display font-bold text-base sm:text-lg truncate flex items-center justify-center gap-1 text-slate-100">
        {bot && <Bot className="w-4 h-4 text-violet-300" />}
        {name} {you && <span className="text-xs text-amber-300 font-mono-stat">(YOU)</span>}
      </div>
      <div className="font-mono-stat text-4xl sm:text-5xl font-black mt-1 text-neon-cyan" data-testid={`${testId}-score`}>{score}</div>
      {you && typeof currentStreak === "number" && typeof bestStreak === "number" && (
        <div className="mt-2 flex justify-center" data-testid={`${testId}-streaks`}>
          <StreakBadges current={currentStreak} best={bestStreak} />
        </div>
      )}
    </div>
  );
}
